import { APPEARANCE, useThemeContext } from "@circle-react/providers/Theme";
import { TextInput } from "@circleco/compass/components/TextInput";
import { useEffect, useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import { useEmojiPickerV4Context } from "../../EmojiPickerV4Context";
import type { CustomEmojiShape } from "../../types";

interface IconPickerProps {
  dismiss: () => void;
}

export const IconPicker = ({ dismiss }: IconPickerProps) => {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const {
    customIcons = [],
    customIconsDark = [],
    onEmojiSelect,
    name,
    shouldCloseOnSelect,
  } = useEmojiPickerV4Context();
  const { currentAppearance } = useThemeContext();

  const iconGroups =
    currentAppearance === APPEARANCE.DARK ? customIconsDark : customIcons;

  const groupedIcons = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return iconGroups
      .map(group => ({
        id: group.id,
        name: group.name,
        icons: query
          ? group.emojis.filter(icon => icon.name.toLowerCase().includes(query))
          : group.emojis,
      }))
      .filter(g => g.icons.length > 0);
  }, [iconGroups, searchValue]);

  const handleIconClick = (icon: CustomEmojiShape) => {
    onEmojiSelect?.({ identifier: icon.id, emoji_type: "icons" }, name);
    if (shouldCloseOnSelect) dismiss();
  };

  return (
    <div className="flex flex-col gap-3 pt-3">
      <TextInput
        ref={searchInputRef}
        icon="magnifying-glass"
        placeholder={t("emoji_picker_v4.search_icons_placeholder")}
        value={searchValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchValue(e.target.value)
        }
      />
      <div className="h-[320px] overflow-y-auto">
        {groupedIcons.map(group => (
          <div key={group.id}>
            <div
              className="grid gap-0"
              style={{
                gridTemplateColumns: "repeat(auto-fill, 34px)",
                justifyContent: "space-between",
              }}
            >
              {group.icons.map(icon => (
                <button
                  key={icon.id}
                  type="button"
                  className="hover:bg-tertiary flex size-[34px] items-center justify-center rounded-lg"
                  onClick={() => handleIconClick(icon)}
                  title={icon.name}
                >
                  <img
                    loading="eager"
                    src={icon.skins[0]?.src}
                    alt={icon.name}
                    className="size-6 object-contain"
                  />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
